import { useEffect, useMemo, useState } from "react";
import { CheckCircle2, Undo2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { useApi, useResource, errorText } from "@/lib/api";
import { formatAUD, sumDecimals } from "@/lib/format";

const slug = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-");

const EditCell = ({ row, field, placeholder, onSave, className = "" }) => {
  const [v, setV] = useState(row[field] || "");
  useEffect(() => { setV(row[field] || ""); }, [row, field]);
  const commit = () => { if (v.trim() !== (row[field] || "").trim()) onSave(row, field, v.trim()); };
  return (
    <input value={v} placeholder={placeholder} onChange={(e) => setV(e.target.value)} onBlur={commit}
      onKeyDown={(e) => { if (e.key === "Enter") e.currentTarget.blur(); if (e.key === "Escape") { setV(row[field] || ""); } }}
      className={`w-full min-w-[110px] bg-transparent rounded-md border border-transparent px-2 py-1 hover:border-border focus:border-primary focus:outline-none transition-colors ${className}`}
      data-testid={`invoice-${field.replace("_", "-")}-${row.id}`} />
  );
};

const Group = ({ name, rows, invoiced, onMark, onSave }) => (
  <div className="border border-border bg-card" data-testid={`invoice-group-${slug(name)}`}>
    <div className="flex flex-wrap items-center gap-3 px-5 py-4 border-b border-border">
      <h3 className="font-display text-lg font-semibold">{name}</h3>
      <span className="text-sm text-muted-foreground">{rows.length} {rows.length === 1 ? "expense" : "expenses"}</span>
      <span className="font-mono text-lg ml-auto" data-testid={`invoice-group-total-${slug(name)}`}>{formatAUD(sumDecimals(rows.map((r) => r.spent)))}</span>
      <Button size="sm" variant={invoiced ? "ghost" : "default"} className="rounded-full" onClick={() => onMark(rows.map((r) => r.id), !invoiced)}
        data-testid={`invoice-group-mark-${slug(name)}`}>
        {invoiced ? <><Undo2 className="h-4 w-4 mr-1" /> Move all back</> : <><CheckCircle2 className="h-4 w-4 mr-1" /> Mark all invoiced</>}
      </Button>
    </div>
    <table className="w-full text-sm">
      <tbody>
        {rows.map((r) => (
          <tr key={r.id} className="border-b border-border/60 last:border-0 hover:bg-accent/40 transition-colors" data-testid={`invoice-row-${r.id}`}>
            <td className="pl-5 py-2.5 w-10">
              <Checkbox checked={!!r.invoiced} onCheckedChange={(v) => onMark([r.id], !!v)} data-testid={`invoice-tick-${r.id}`} aria-label="Invoiced" />
            </td>
            <td className="px-2 font-mono text-xs whitespace-nowrap text-muted-foreground">{r.date}</td>
            <td className="px-2">{r.description}</td>
            <td className="px-1 w-[16%]"><EditCell row={r} field="for_whom" placeholder="For" onSave={onSave} /></td>
            <td className="px-1 w-[20%]"><EditCell row={r} field="what" placeholder="What" onSave={onSave} className="text-muted-foreground" /></td>
            <td className="px-1 w-[18%]"><EditCell row={r} field="project" placeholder="Project" onSave={onSave} /></td>
            <td className="pr-5 py-2.5 text-right font-mono">{formatAUD(r.spent)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

export default function InvoicingPage() {
  const api = useApi();
  const { data, setData } = useResource("/rebill");
  const [tab, setTab] = useState("todo");
  const rows = data || [];
  const todo = rows.filter((r) => !r.invoiced);
  const done = rows.filter((r) => r.invoiced);
  const groups = useMemo(() => {
    const by = new Map();
    for (const r of tab === "todo" ? todo : done) {
      const k = r.for_whom.trim();
      by.set(k, [...(by.get(k) || []), r]);
    }
    return [...by.entries()].sort((a, b) => a[0].localeCompare(b[0]));
  }, [tab, todo, done]);

  const mark = async (ids, invoiced) => {
    const set = new Set(ids);
    setData((l) => l.map((r) => (set.has(r.id) ? { ...r, invoiced } : r)));
    try {
      await api("post", "/rebill/mark", { ids, invoiced });
      toast.success(invoiced ? `Marked ${ids.length} as invoiced` : `Moved ${ids.length} back to be invoiced`);
    } catch (e) {
      setData((l) => l.map((r) => (set.has(r.id) ? { ...r, invoiced: !invoiced } : r)));
      toast.error(errorText(e));
    }
  };
  const save = async (row, field, value) => {
    if (field === "for_whom" && !value && !window.confirm("Clearing For removes this expense from Invoicing. Continue?")) {
      setData((l) => l.map((r) => ({ ...r })));
      return;
    }
    try {
      const upd = await api("put", `/transactions/${row.id}`, { [field]: value });
      setData((l) => (field === "for_whom" && !value ? l.filter((r) => r.id !== row.id) : l.map((r) => (r.id === row.id ? { ...r, ...upd } : r))));
      toast.success(field === "for_whom" && !value ? "Removed from invoicing" : "Saved");
    } catch (e) {
      setData((l) => l.map((r) => ({ ...r })));
      toast.error(errorText(e));
    }
  };
  const Tab = ({ v, label, list }) => (
    <button type="button" onClick={() => setTab(v)} data-testid={`invoice-tab-${v}`}
      className={`px-5 py-2.5 rounded-xl text-left transition-colors ${tab === v ? "bg-primary/10 text-primary" : "text-muted-foreground hover:text-foreground"}`}>
      <div className="text-sm font-semibold">{label} ({list.length})</div>
      <div className="font-mono text-lg" data-testid={`invoice-tab-total-${v}`}>{formatAUD(sumDecimals(list.map((r) => r.spent)))}</div>
    </button>
  );

  return (
    <div className="rise">
      <h1 className="font-display text-4xl sm:text-5xl font-semibold" data-testid="invoicing-title">Invoicing</h1>
      <p className="mt-3 text-muted-foreground text-base md:text-lg">Expenses you paid for someone else. Any spent row with "For" filled in lands here so you remember to invoice it. Click For, What or Project to edit it here.</p>
      <div className="mt-8 inline-flex gap-1 border border-border bg-card p-1">
        <Tab v="todo" label="To be invoiced" list={todo} />
        <Tab v="done" label="Invoiced" list={done} />
      </div>
      <div className="mt-6 space-y-5">
        {groups.map(([name, list]) => <Group key={name} name={name} rows={list} invoiced={tab === "done"} onMark={mark} onSave={save} />)}
        {data && groups.length === 0 && (
          <p className="text-muted-foreground" data-testid="invoice-empty">
            {tab === "todo" ? "Nothing to invoice. Fill in \"For\" on a spent ledger row and it shows up here." : "Nothing marked as invoiced yet."}
          </p>
        )}
      </div>
    </div>
  );
}
